/**
 * Fechas civiles (YYYY-MM-DD) sin salirse del día.
 *
 * `new Date().toISOString().slice(0, 10)` da el día **en UTC**, no el del
 * usuario. Desde Colombia (UTC-5) eso desplaza el resultado un día en dos
 * situaciones distintas:
 *
 *  · a partir de las 19:00, «hoy» pasa a ser mañana;
 *  · una fecha a la que se le puso `setHours(23,59,59)` cae ya en el día
 *    siguiente — así, el fin de periodo por defecto de las liquidaciones
 *    proponía el 1 de octubre en vez del 30 de septiembre.
 *
 * `toCivilDate` formatea el instante en la zona del navegador, que es la
 * de quien está mirando la pantalla.
 */
export function toCivilDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  // `en-CA` devuelve YYYY-MM-DD nativamente.
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** Hoy como YYYY-MM-DD, en la zona del navegador. */
export function todayCivil(): string {
  return toCivilDate(new Date());
}

/**
 * Format a date to a localized string
 */
export function formatDate(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  });
}

/**
 * Format a number to currency
 */
export function formatCurrency(amount: number, currency: string = 'COP'): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format a date to a relative time string (e.g., "hace 2 horas")
 */
export function formatRelativeTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  const intervals = [
    { label: 'año', seconds: 31536000 },
    { label: 'mes', seconds: 2592000 },
    { label: 'semana', seconds: 604800 },
    { label: 'día', seconds: 86400 },
    { label: 'hora', seconds: 3600 },
    { label: 'minuto', seconds: 60 },
    { label: 'segundo', seconds: 1 },
  ];

  for (const interval of intervals) {
    const count = Math.floor(diffInSeconds / interval.seconds);
    if (count >= 1) {
      const plural = count > 1 ? 's' : '';
      return `hace ${count} ${interval.label}${plural}`;
    }
  }

  return 'ahora mismo';
}

/**
 * Capitalize the first letter of a string
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Truncate a string to a specified length
 */
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return `${str.slice(0, length)}...`;
}

/**
 * Generate initials from a name
 */
export function getInitials(name: string): string {
  // `split(' ')` dejaba palabras vacías con espacios dobles o al inicio
  // («  Ana   Pérez» → «A» o nada) y el avatar salía sin iniciales.
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

/**
 * Sleep for a specified number of milliseconds
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Debounce a function
 */
export function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), wait);
  };
}

/**
 * Format a number with locale separators
 */
export function formatNumber(value: number, locale: string = 'es-CO'): string {
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Format a number as percentage
 */
export function formatPercent(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a date to short format (e.g., "2 mar 2025")
 */
export function formatShortDate(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Format a date with time (e.g., "2 mar 2025, 14:30")
 */
export function formatDateTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format currency in compact form (e.g., "$1.2M", "$500K")
 */
export function formatCompactCurrency(amount: number, currency: string = 'COP'): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency,
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(amount);
}
